import { useNavigate, useParams } from 'react-router'
import { KlearDate } from '@klear/date'
import { DataTable } from '@klear/ui/components/data-table'
import { useTrader } from '@klear/ui/hooks/use-trader'
import { dateFormatted, listItemDateEpochDay } from '@klear/order-event/list-display'
import { formatSummaryOrderValue, summaryOrderValueSortKey } from '@klear/order/list-display'
import AddOrder from '@/app/orders/add-order'
import { OrdersInsights } from '@/app/orders/orders-insights'
import { usePurchaseOrders } from '@klear/ui/hooks/use-purchase-orders'
import { useOrders, type OrderListRead } from '@klear/ui/hooks/use-orders'
import { PERMISSION_ENTITY_ORDER, PERMISSION_ACTION_EDIT, ROUTE_ORDERS } from '@klear/constants'
import RowActions from '@/components/row-actions'
import { useOrderMutations } from '@klear/ui/hooks/use-order'
import { DataTableColumnHeader } from '@klear/ui/components/data-table-column-header'
import type { ColumnDef } from '@tanstack/react-table'
import { useMemo, useState } from 'react'
import { Button } from '@klear/ui/components/button'
import EventActionsBulkConfirmModal from '@klear/order-event-ui/event-actions-bulk-confirm-modal'
import { useAuth } from '@klear/ui/contexts/auth-context'
import noop from 'lodash/noop'
import type { Order } from '@klear/order'
import { SegmentedControl } from '@klear/ui/components/segmented-control'
import { Archive, CircleCheck } from 'lucide-react'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

type OrderListTab = 'active' | 'archived'

function useDeliveryDateByOrderId(traderId: number): Map<number, KlearDate> {
  const filters = traderId > 0 ? { traderId } : {}
  const { purchaseOrders } = usePurchaseOrders(filters)
  return useMemo(() => {
    const byOrderId = new Map<number, KlearDate>()
    for (const po of purchaseOrders) {
      const existing = byOrderId.get(po.orderId)
      if (existing == null || po.endDate.isAfter(existing)) byOrderId.set(po.orderId, po.endDate)
    }
    return byOrderId
  }, [purchaseOrders])
}

const createColumns = (
  urlPrefixApp: string,
  traderId: number,
  deleteMutation: (params: { orderId: number; traderId: number }) => Promise<unknown>,
  archiveMutation: (params: { id: number; isArchived: boolean }) => Promise<unknown>,
  isArchived: boolean,
  isDeleting: boolean,
  isArchiving: boolean,
  deliveryDateByOrderId: Map<number, KlearDate>
) => {
  const columns: ColumnDef<OrderListRead>[] = [
    {
      id: 'idDisplay',
      accessorFn: (row) => row.idDisplay,
      header: ({ column }) => <DataTableColumnHeader column={column} title="Order ID" />,
      cell: ({ row }) => <div className="font-medium">{row.original.idDisplay}</div>,
      enableSorting: true,
      enableHiding: true
    },
    {
      id: 'customer',
      accessorFn: (row) => row.customerOrganizationName,
      header: ({ column }) => <DataTableColumnHeader column={column} title="Customer" />,
      cell: ({ row }) => <div className="font-medium">{row.original.customerOrganizationName}</div>,
      enableSorting: true,
      enableHiding: true
    },
    {
      id: 'orderValue',
      accessorFn: (row) => formatSummaryOrderValue(row.listSummary),
      header: ({ column }) => <DataTableColumnHeader align="right" column={column} title="Order Value" />,
      cell: ({ row }) => <div className="text-right">{formatSummaryOrderValue(row.original.listSummary)}</div>,
      enableSorting: true,
      enableHiding: true,
      sortingFn: (rowA, rowB) =>
        summaryOrderValueSortKey(rowA.original.listSummary).compare(summaryOrderValueSortKey(rowB.original.listSummary))
    },
    {
      id: 'effectiveDate',
      accessorFn: (row) => dateFormatted(row.listSummary?.effectiveDate) ?? '',
      header: ({ column }) => <DataTableColumnHeader align="right" column={column} title="Effective Date" />,
      cell: ({ row }) => (
        <div className="text-right">{dateFormatted(row.original.listSummary?.effectiveDate) ?? ''}</div>
      ),
      enableSorting: true,
      enableHiding: true,
      sortingFn: (rowA, rowB) =>
        listItemDateEpochDay(rowA.original.listSummary?.effectiveDate) -
        listItemDateEpochDay(rowB.original.listSummary?.effectiveDate)
    },
    {
      id: 'deliveryDate',
      accessorFn: (row) => deliveryDateByOrderId.get(row.id)?.epochDay ?? 0,
      header: ({ column }) => <DataTableColumnHeader align="right" column={column} title="Target Delivery Date" />,
      cell: ({ row }) => (
        <div className="text-right">{dateFormatted(deliveryDateByOrderId.get(row.original.id)) ?? '—'}</div>
      ),
      enableSorting: true,
      enableHiding: true
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <div className="flex justify-end">
          <RowActions
            row={row}
            appPath={`${urlPrefixApp}/${ROUTE_ORDERS}`}
            resourceType={PERMISSION_ENTITY_ORDER}
            getResourceName={(order) => `Order ${order.idDisplay}`}
            deleteMutation={({ id }) => deleteMutation({ orderId: id, traderId })}
            isDeleting={isDeleting}
            isArchived={isArchived}
            archiveMutation={archiveMutation}
            isArchiving={isArchiving}
          />
        </div>
      )
    }
  ]

  return columns
}

export default function Orders() {
  useDocumentTitle('Orders')
  const navigate = useNavigate()
  const { traderId } = useParams()
  const { user } = useAuth()
  const { urlPrefixApp } = useTrader(traderId)
  const [activeTab, setActiveTab] = useState<OrderListTab>('active')
  const isArchivedTab = activeTab === 'archived'
  const parsedTraderId = parseInt(traderId || '0')
  const { orders, isLoading } = useOrders(parsedTraderId, { isArchived: isArchivedTab })
  const deliveryDateByOrderId = useDeliveryDateByOrderId(parsedTraderId)
  const { deleteOrder, setOrderArchived, isDeleting, isArchiving } = useOrderMutations()
  const [bulkUpdateModalOpen, setBulkUpdateModalOpen] = useState<boolean>(false)

  const canEditOrders = user?.hasPermission(PERMISSION_ENTITY_ORDER, PERMISSION_ACTION_EDIT) ?? false

  const columns = useMemo(
    () =>
      createColumns(
        urlPrefixApp,
        parsedTraderId,
        deleteOrder,
        ({ id, isArchived }) => setOrderArchived({ orderId: id, isArchived }),
        isArchivedTab,
        isDeleting,
        isArchiving,
        deliveryDateByOrderId
      ),
    [
      urlPrefixApp,
      parsedTraderId,
      deleteOrder,
      setOrderArchived,
      isArchivedTab,
      isDeleting,
      isArchiving,
      deliveryDateByOrderId
    ]
  )

  const handleCreateSuccess = (order: Order) => {
    if (order.id != null) {
      navigate(`${urlPrefixApp}/${ROUTE_ORDERS}/${order.id}`)
    }
  }

  const handleBulkUpdateModalClose = () => {
    setBulkUpdateModalOpen(false)
  }

  const handleBulkUpdateSuccess = noop

  return (
    <>
      <div className="h-full flex-1 flex-col space-y-8 p-8 md:flex">
        <div className="flex items-center justify-between space-y-2">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Orders</h2>
            <p className="text-muted-foreground">View and manage your orders.</p>
          </div>
          <div className="flex items-center gap-2">
            {canEditOrders && (
              <Button onClick={() => setBulkUpdateModalOpen(true)} variant="outline">
                Bulk Update
              </Button>
            )}
            <AddOrder onSuccess={handleCreateSuccess} />
          </div>
        </div>
        <OrdersInsights />
        <div className="space-y-4">
          <div className="flex justify-end">
            <SegmentedControl
              className="w-104"
              options={[
                {
                  value: 'active',
                  label: 'Active',
                  count: activeTab === 'active' ? orders.length : undefined,
                  icon: <CircleCheck className="h-4 w-4" />
                },
                {
                  value: 'archived',
                  label: 'Archived',
                  count: activeTab === 'archived' ? orders.length : undefined,
                  icon: <Archive className="h-4 w-4" />
                }
              ]}
              value={activeTab}
              onChange={setActiveTab}
            />
          </div>
          <DataTable data={orders} columns={columns} filters={[]} loading={isLoading} />
        </div>
      </div>
      <EventActionsBulkConfirmModal
        isOpen={bulkUpdateModalOpen}
        onClose={handleBulkUpdateModalClose}
        onSuccess={handleBulkUpdateSuccess}
        traderId={traderId}
      />
    </>
  )
}
